import type {
  CancelUnpaidOrderInput,
  CustomerOrderDetail,
  RequestOrderCancelInput,
  UpdateOrderAddressInput,
} from '@repo/api-contracts';
import { type ReactElement, useRef, useState } from 'react';
import { orderDestinationsForDocument } from '@/entities/order';
import { ReviewOrderAction, useReviewStatesForOrdersQuery } from '@/entities/review';
import { OrderUnpaidCancelDialog } from '@/features/order-cancel';
import { OrderCancelDialog } from '@/features/order-cancel-request';
import { OrderAddressDialog } from '@/features/order-shipping-address';
import { isOrderConflict, orderMutationMessage, safeHttpsUrl } from '@/shared/lib';
import { QuoteDocument } from '@/shared/ui';
import { OrderActions } from './order-actions';
import { OrderCancelRequests } from './order-cancel-requests';
import { OrderContent } from './order-content';
import { OrderDestinations } from './order-destinations';
import { OrderDetailHeader } from './order-detail-header';
import { OrderPaymentSummary } from './order-payment-summary';
import { OrderProgressSteps } from './order-progress-steps';
import { OrderSchedule } from './order-schedule';
import { OrderSummary } from './order-summary';

interface MyOrderDetailProps {
  readonly order: CustomerOrderDetail;
  readonly onCancelUnpaid: (input: CancelUnpaidOrderInput) => Promise<void>;
  readonly onRequestCancel: (input: RequestOrderCancelInput) => Promise<void>;
  readonly onUpdateAddress: (input: UpdateOrderAddressInput) => Promise<void>;
  /** 상태 충돌(ORDER_STATE_CONFLICT) 때 상세를 다시 읽는다. 성공한 쓰기는 쓰기 훅이 응답 상세로 캐시를 바꾼다. */
  readonly onReload: () => void;
  readonly pending?: boolean;
  readonly error?: string | null;
  readonly quoteHref?: string;
}

/** 견적 보기 주소 — 앱 안 경로(/…, //·\ 제외)이거나 안전한 https 일 때만 링크로 쓴다. */
const safeQuoteUrl = (href: string | undefined): string | null =>
  href?.startsWith('/') && !href.startsWith('//') && !href.includes('\\')
    ? href
    : safeHttpsUrl(href);

/**
 * 내 주문 상세(주문 설계 §8-2) — 내 견적 상세와 같은 짜임: 머리(주문 번호·상태·주문일), 진행 단계, 왼쪽 큰 칸(주문 내용),
 * 오른쪽 칸(주문 정보·처리 버튼·결제·배송지 카드(배송지마다 송장·배송지 변경)·배송 일정·취소 요청 내역).
 * "견적서 인쇄"는 주문한 발행본을 견적서(QuoteDocument — 공급자·수신자 포함, 견적 설계 §7-10)로 브라우저 인쇄창에 낸다.
 * 화면에는 주문 내용이 같은 내용을 보여 주므로 견적서는 인쇄 때만 보이고 나머지는 인쇄에서 숨긴다.
 */
export const MyOrderDetail = ({
  order,
  onCancelUnpaid,
  onRequestCancel,
  onUpdateAddress,
  onReload,
  pending = false,
  error,
  quoteHref,
}: MyOrderDetailProps): ReactElement => {
  const [dialog, setDialog] = useState<
    { kind: 'cancel' } | { kind: 'address'; destinationId: string } | { kind: 'unpaid' } | null
  >(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const lock = useRef(false);
  const reviewStates = useReviewStatesForOrdersQuery([order.id]);
  /** 주소를 바꾸는 배송지 — 상태 충돌로 다시 읽은 주문에 그 배송지가 없으면 대화상자를 그리지 않는다. */
  const addressTarget =
    dialog?.kind === 'address'
      ? order.destinations.find((destination) => destination.id === dialog.destinationId)
      : undefined;

  /** 결제 대기일 때만 결제 전 취소를 보낼 수 있다 — 상태 충돌로 다시 읽은 주문이 결제됐으면 확인 버튼을 막는다. */
  const canCancelUnpaid = order.status === 'PENDING_PAYMENT';

  const openUnpaidDialog = (): void => {
    // 지난번 실패 안내를 들고 다시 열지 않는다.
    setMessage(null);
    setDialog({ kind: 'unpaid' });
  };

  const cancelUnpaid = async (): Promise<void> => {
    if (pending || lock.current || !canCancelUnpaid) return;
    lock.current = true;
    setSaving(true);
    setMessage(null);
    try {
      // 성공 뒤 상세는 쓰기 훅이 응답(최신 상세)으로 갱신한다 — 같은 상세를 다시 읽지 않는다.
      await onCancelUnpaid({ orderId: order.id });
      setDialog(null);
    } catch (failure) {
      setMessage(orderMutationMessage(failure));
      if (isOrderConflict(failure)) onReload();
    } finally {
      lock.current = false;
      setSaving(false);
    }
  };

  const version = order.quoteVersion;
  const receiptUrl =
    order.status !== 'PENDING_PAYMENT' && order.status !== 'CANCELED'
      ? safeHttpsUrl(order.payment?.receiptUrl)
      : null;
  const hasSchedule =
    order.status === 'PAID' || order.expectedShipDate !== null || order.delayNotices.length > 0;

  return (
    <div className="flex flex-col gap-20 sm:gap-24">
      <div className="flex flex-col gap-10 print:hidden">
        {error ? (
          <div
            role="alert"
            className="flex flex-col items-center gap-4 border-y border-foreground/10 py-12 text-center"
          >
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : null}

        <OrderDetailHeader order={order} />
        <OrderProgressSteps order={order} />

        <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:gap-16">
          <OrderContent order={order} />

          <div className="flex flex-col gap-8">
            <OrderSummary order={order} quoteUrl={safeQuoteUrl(quoteHref)} />
            {/* 대화상자가 열려 있어도 여는 버튼을 비활성으로 바꾸지 않는다 — 비활성이 된 버튼은 브라우저가 포커스를 body 로 옮겨
                닫힐 때 돌아갈 곳을 잃는다. 열린 동안 바깥 클릭은 모달(inert)이 막는다. */}
            <OrderActions
              order={order}
              pending={pending || saving}
              onCancelUnpaid={openUnpaidDialog}
              onRequestCancel={() => setDialog({ kind: 'cancel' })}
              onPrintQuote={() => window.print()}
            />
            <ReviewOrderAction state={reviewStates.data?.get(order.id)} />
            {order.payment !== null ? (
              <OrderPaymentSummary
                payment={order.payment}
                receiptUrl={receiptUrl}
                bankTransfer={order.bankTransfer}
                taxInvoice={order.taxInvoice}
              />
            ) : null}
            <OrderDestinations
              order={order}
              pending={pending || saving}
              onChangeAddress={(destinationId) => setDialog({ kind: 'address', destinationId })}
            />
            {hasSchedule ? (
              <OrderSchedule
                expectedShipDate={order.expectedShipDate}
                delayNotices={order.delayNotices}
              />
            ) : null}
            {order.cancelRequests.length > 0 ? (
              <OrderCancelRequests requests={order.cancelRequests} />
            ) : null}
          </div>
        </div>

        {dialog?.kind === 'cancel' ? (
          <OrderCancelDialog
            orderId={order.id}
            requiresRefundAccount={
              order.payment?.provider === 'BANK_TRANSFER' && order.status === 'PAID'
            }
            onSubmit={onRequestCancel}
            onReload={onReload}
            onClose={() => setDialog(null)}
            pending={pending}
          />
        ) : null}
        {addressTarget !== undefined ? (
          <OrderAddressDialog
            orderId={order.id}
            destinationId={addressTarget.id}
            destinationNumber={order.destinations.indexOf(addressTarget) + 1}
            region={addressTarget.region}
            address={addressTarget.shippingAddress}
            onSubmit={onUpdateAddress}
            onReload={onReload}
            onClose={() => setDialog(null)}
            pending={pending}
          />
        ) : null}
        {dialog?.kind === 'unpaid' ? (
          <OrderUnpaidCancelDialog
            onConfirm={() => void cancelUnpaid()}
            onClose={() => setDialog(null)}
            pending={pending || saving}
            confirmable={canCancelUnpaid}
            message={message}
          />
        ) : null}
      </div>
      <QuoteDocument
        className="hidden print:flex"
        version={version}
        quoteNumber={order.quoteNumber}
        destinations={orderDestinationsForDocument(order.destinations)}
      />
    </div>
  );
};
